import { useCallback, useEffect, useState } from "react";
import { Loader2, Download, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatErr } from "@/lib/api";
import { money } from "@/pages/Awards";
import { PRO_LABEL } from "@/components/ProsManager";
import { toast } from "sonner";

const uiLocale = () => (localStorage.getItem("gv_tour_lang") === "es" ? "es" : "en-US");
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString(uiLocale(), { month: "long", year: "numeric" });

function Rates() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/admin/payout-rates").then((x) => setR(x.data)); }, []);
  if (!r) return null;
  const save = async () => { try { setR((await api.put("/admin/payout-rates", r)).data); toast.success("Payout rates saved (applies to future completions)"); } catch (e) { toast.error(formatErr(e)); } };
  return (
    <div className="flex flex-wrap items-end gap-3 bg-white border border-slate-200 rounded-lg p-4" data-testid="payout-rates">
      <span className="text-sm font-semibold text-slate-900 w-full md:w-auto md:mr-2">Flat payout per completed job</span>
      {Object.entries(PRO_LABEL).map(([k, l]) => (
        <label key={k} className="text-xs text-slate-500">{l}<Input type="number" min="0" className="mt-1 w-28" value={r[k]} onChange={(e) => setR({ ...r, [k]: Number(e.target.value) })} data-testid={`payout-rate-${k}`} /></label>
      ))}
      <Button size="sm" onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="payout-rates-save-button">Save rates</Button>
    </div>
  );
}

function exportCsv(d) {
  const rows = [["Pro", "Type", "Job", "Client", "Completed", "Amount", "Status"], ...d.pros.flatMap((p) => p.items.map((i) => [p.pro_name, p.pro_type, i.title, i.client_email, i.completed_at, i.amount, i.status]))];
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n")], { type: "text/csv" }));
  a.download = `payouts_${d.month}.csv`;
  a.click();
}

export function PayoutsPanel() {
  const [month, setMonth] = useState("");
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/admin/payouts", { params: month ? { month } : {} }).then((r) => setD(r.data)), [month]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { window.addEventListener("gv:w9-changed", load); return () => window.removeEventListener("gv:w9-changed", load); }, [load]);
  const pay = async (p) => {
    if (!window.confirm(`Mark ${money(p.owed)} as paid to ${p.pro_name} for ${monthLabel(d.month)}?`)) return;
    try {
      const { data } = await api.post("/admin/payouts/mark-paid", { pro_id: p.pro_id, month: d.month });
      toast.success(data.statement_id ? `Marked as paid · PDF statement emailed to ${p.pro_name}` : "Marked as paid");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  if (!d) return <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />;
  return (
    <div className="space-y-3" data-testid="payouts-panel">
      <Rates />
      <div className="flex flex-wrap items-center gap-3">
        <Select value={d.month} onValueChange={setMonth}>
          <SelectTrigger className="w-52" data-testid="payout-month-select"><SelectValue /></SelectTrigger>
          <SelectContent>{d.months.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-sm text-slate-600">Owed <b className="text-red-600" data-testid="payout-total-owed">{money(d.total_owed)}</b> · Paid <b className="text-emerald-700">{money(d.total_paid)}</b></span>
        <Button size="sm" variant="outline" className="ml-auto" onClick={() => exportCsv(d)} data-testid="payout-export-button"><Download className="h-4 w-4 mr-1" />Export CSV</Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
        {d.pros.length === 0 && <p className="p-5 text-sm text-slate-500" data-testid="payouts-empty">No completed assignments this month.</p>}
        {d.pros.map((p) => (
          <details key={p.pro_id} className="p-4" data-testid={`payout-pro-${p.pro_id}`}>
            <summary className="flex flex-wrap items-center gap-3 cursor-pointer list-none">
              <span className="flex-1 font-semibold text-sm text-slate-900">{p.pro_name} <span className="text-[10px] font-mono uppercase text-amber-700 ml-1">{PRO_LABEL[p.pro_type]}</span></span>
              <span className="text-xs text-slate-500">{p.jobs} job{p.jobs === 1 ? "" : "s"}</span>
              <span className="text-sm">Owed <b className={p.owed ? "text-red-600" : "text-slate-400"}>{money(p.owed)}</b></span>
              <span className="text-sm text-emerald-700">Paid {money(p.paid)}</span>
              {p.w9_status !== "approved" && <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200" data-testid={`payout-w9-hold-${p.pro_id}`}>On hold · W-9 {p.w9_status}</span>}
              {p.owed > 0 && <Button size="sm" onClick={(e) => { e.preventDefault(); pay(p); }} disabled={p.w9_status !== "approved"} title={p.w9_status !== "approved" ? "Approve this pro's W-9 first" : undefined} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`payout-mark-paid-${p.pro_id}`}>Mark paid</Button>}
            </summary>
            <div className="mt-3 space-y-1">
              {p.items.map((i) => <div key={i.payout_id} className="text-xs flex justify-between text-slate-600"><span>{i.title} · {i.client_email}</span><span>{money(i.amount)} · {i.status}</span></div>)}
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}

export function ProEarnings() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/pro/earnings").then((r) => setD(r.data)); }, []);
  if (!d) return null;
  const cur = d.months[0];
  return (
    <div className="bg-slate-900 text-white rounded-lg p-6" data-testid="pro-earnings">
      <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-emerald-400"><Wallet className="h-4 w-4" />My earnings · {money(d.rate)} per completed job</div>
      <div className="mt-4 grid grid-cols-3 gap-4">
        <div><div className="text-xs text-slate-400">Owed to you</div><div className="font-display text-3xl font-extrabold" data-testid="pro-earnings-owed">{money(d.months.reduce((s, m) => s + m.owed, 0))}</div>{d.w9_status !== "approved" && d.months.reduce((s, m) => s + m.owed, 0) > 0 && <div className="text-[11px] text-amber-300 mt-0.5" data-testid="pro-earnings-w9-hold">On hold until your W-9 is approved</div>}</div>
        <div><div className="text-xs text-slate-400">Paid out</div><div className="font-display text-3xl font-extrabold text-emerald-400">{money(d.months.reduce((s, m) => s + m.paid, 0))}</div></div>
        <div><div className="text-xs text-slate-400">{cur ? monthLabel(cur.month) : "This month"}</div><div className="font-display text-3xl font-extrabold">{cur?.jobs || 0} job{cur?.jobs === 1 ? "" : "s"}</div></div>
      </div>
      <Statements />
    </div>
  );
}

async function downloadStatement(s) {
  const res = await api.get(`/statements/${s.statement_id}/pdf`, { responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(res.data);
  a.download = `GrantVerify_statement_${s.month}_${s.statement_id}.pdf`;
  a.click();
}

function Statements() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/pro/statements").then((r) => setList(r.data)); }, []);
  if (!list.length) return null;
  return (
    <div className="mt-6 border-t border-slate-700 pt-4" data-testid="pro-statements">
      <div className="text-xs text-slate-400 mb-2">Payout statements</div>
      <div className="space-y-2">{list.map((s) => (
        <div key={s.statement_id} className="flex items-center gap-3 text-sm" data-testid={`pro-statement-${s.statement_id}`}>
          <span className="flex-1">{monthLabel(s.month)} · {s.jobs} job{s.jobs === 1 ? "" : "s"} · paid {new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s.paid_at) ? s.paid_at : `${s.paid_at}Z`).toLocaleDateString(uiLocale())}</span>
          <b className="text-emerald-400">{money(s.total)}</b>
          <Button size="sm" variant="outline" onClick={() => downloadStatement(s)} className="h-7 bg-transparent border-slate-600 text-white hover:bg-slate-800 hover:text-white" data-testid={`pro-statement-download-${s.statement_id}`}><Download className="h-3.5 w-3.5 mr-1" />PDF</Button>
        </div>
      ))}</div>
    </div>
  );
}
